import { submitPost } from "deso-protocol";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { use, useCallback } from "react";

import { waitForTxn } from "@/backend/api-helpers";
import type { CreateCommentDTO } from "@/components/app/post/post-comments/use-create-comment";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import { useBooleanState } from "@/hooks/use-boolean-state";

import { useUpvotePost } from "./use-upvote-post";

type SharePostTransaction = {
  postHashHex?: string;
  transactionHash: string;
};

type SharePostOptions = {
  onTransactionSubmitted?: (
    transaction: SharePostTransaction,
  ) => Promise<void> | void;
  toleratePostConfirmationFailure?: boolean;
};

export const useSharePost = (postHash: string) => {
  const { currentUserPublicKey } = use(UserContext);

  const [loading, { setTrue: setLoading, setFalse: unsetLoading }] =
    useBooleanState(false);
  const { upvotePost } = useUpvotePost();

  const router = useRouter();

  const onShare = useCallback(
    async (
      data?: CreateCommentDTO,
      isQuotedRepost = false,
      options: SharePostOptions = {},
    ) => {
      if (isQuotedRepost && !data) return;

      const { body, extraData } = data || {};

      if (loading) return;

      let transactionSubmitted = false;

      try {
        setLoading();

        const data = await submitPost({
          UpdaterPublicKeyBase58Check: currentUserPublicKey,
          RepostedPostHashHex: postHash,
          PostHashHexToModify: "",
          IsFrozen: false,
          IsHidden: false,
          BodyObj: body || {
            Body: "",
            VideoURLs: null,
            ImageURLs: null,
          },
          PostExtraData: { ...extraData, isMarkdown: "true" },
        });

        if (!data.submittedTransactionResponse?.TxnHashHex) {
          return Promise.reject(
            "Transaction was not constructed. Please try again.",
          );
        }

        const transactionHash = data.submittedTransactionResponse.TxnHashHex;
        const repostedPostHash =
          data?.submittedTransactionResponse.PostEntryResponse?.PostHashHex;

        transactionSubmitted = true;

        await options.onTransactionSubmitted?.({
          postHashHex: repostedPostHash,
          transactionHash,
        });

        const promises = [];

        if (repostedPostHash) {
          promises.push(upvotePost({ postHash: repostedPostHash }));
        }

        promises.push(
          waitForTxn({
            transactionHash,
          }),
        );

        await Promise.all(promises);

        toast({
          title: "Success",
          description: (
            <>
              {isQuotedRepost
                ? "This post was successfully quoted"
                : "This post was successfully reposted"}
              .
              <br />
              <Link
                href={`/post/${repostedPostHash}`}
                passHref
                target="_blank"
                className="underline"
                prefetch={false}
              >
                View Repost
              </Link>
            </>
          ),
          variant: "success",
        });

        // For normal reposts we don't want to redirect
        if (isQuotedRepost && repostedPostHash) {
          router.push(`/post/${repostedPostHash}`);
        }
      } catch (error) {
        if (transactionSubmitted && options.toleratePostConfirmationFailure) {
          console.error("Post shared, but confirmation failed", error);
          return;
        }

        toast({
          variant: "destructive",
          title: "Error",
          description: `Repost error`,
          details: createToastErrorDetails(error),
        });
      } finally {
        unsetLoading();
      }
    },
    [
      loading,
      postHash,
      currentUserPublicKey,
      router,
      setLoading,
      unsetLoading,
      upvotePost,
    ],
  );

  return { onShare, loading };
};
